import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@clerk/clerk-react";
import { toast } from "@/lib/toast";
import { useCreateNotification } from "@/features/notifications/hooks/useCreateNotification";
import { analyzeCreative, type AnalyzeCreativeOptions } from "../api/analyzeCreative";
import type { CriativoStage } from "../types";

export function useAnalyzeCreative() {
  const [stage, setStage] = useState<CriativoStage>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  const { session } = useSession();
  const createNotification = useCreateNotification();

  const mutation = useMutation({
    mutationFn: async (
      input: Omit<AnalyzeCreativeOptions, "token" | "signal" | "onStageChange">,
    ) => {
      const token = await session?.getToken();
      if (!token) throw new Error("Usuário não autenticado.");
      const controller = new AbortController();
      abortRef.current = controller;
      return analyzeCreative({
        ...input,
        token,
        signal: controller.signal,
        onStageChange: (s) => setStage(s),
      });
    },
    onSuccess: (data) => {
      setStage("done");
      void queryClient.invalidateQueries({ queryKey: ["criativos"] });
      const href = `/criativos/${data.criativoId}`;
      toast.success("Análise de criativo pronta", {
        action: { label: "Abrir", onClick: () => { window.location.assign(href); } },
      });
      void createNotification({
        type: "job_done",
        title: "Análise de criativo pronta",
        href,
        metadata: { jobId: data.jobId, criativoId: data.criativoId },
      });
    },
    onError: () => {
      setStage("error");
    },
  });

  function reset() {
    setStage("idle");
    mutation.reset();
  }

  function abort() {
    abortRef.current?.abort();
    setStage("idle");
    mutation.reset();
  }

  return {
    stage,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    mutate: mutation.mutate,
    reset,
    abort,
  };
}
